/**
 * 服务器连接池状态管理
 */

import { create } from 'zustand';
import type { WSConfig, WSState, WSServerConnection } from '@/types';

interface ServerState {
  // 连接池：按 serverId 存储
  connections: Map<number, WSServerConnection>;
  
  // 当前活跃服务器
  activeServerId: number | null;
  activeServerName: string;
  activeServerIp: string;
  
  // 模态框状态
  isModalOpen: boolean;
  isFullscreen: boolean;
  
  // Actions
  setActiveServer: (id: number, name: string, ip: string) => void;
  
  // 连接管理
  getConnection: (serverId: number) => WSServerConnection | undefined;
  createConnection: (serverId: number, config: WSConfig) => Promise<WSServerConnection>;
  closeConnection: (serverId: number) => void;
  closeAllConnections: () => void;
  
  // 状态更新
  updateConnectionState: (serverId: number, state: WSState) => void;
  
  // 模态框控制
  openModal: () => void;
  closeModal: () => void;
  toggleFullscreen: () => void;
}

export const useServerStore = create<ServerState>((set, get) => ({
  // 初始状态
  connections: new Map(),
  activeServerId: null,
  activeServerName: '',
  activeServerIp: '',
  isModalOpen: false,
  isFullscreen: false,
  
  // 设置活跃服务器
  setActiveServer: (id, name, ip) => {
    set({
      activeServerId: id,
      activeServerName: name,
      activeServerIp: ip,
    });
  },
  
  // 获取连接
  getConnection: (serverId) => {
    return get().connections.get(serverId);
  },
  
  // 创建连接（动态导入 WS_Chat_Client）
  createConnection: async (serverId, config) => {
    const existing = get().connections.get(serverId);
    if (existing && existing.client?.is_connected?.()) {
      return existing;
    }
    
    // 动态导入现有的 WS_Chat_Client
    // @ts-ignore - 全局变量
    const WSClient = window.WS_Chat_Client || (await import('@/ws-client')).WS_Chat_Client;
    
    const client = new WSClient({
      ...config,
      serverId,
      sessionId: `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    });
    
    const connection: WSServerConnection = {
      serverId,
      client,
      state: 'CONNECTING',
      sessionId: client.sessionId,
      reconnectAttempts: 0,
    };
    
    set((state) => {
      const newConnections = new Map(state.connections);
      newConnections.set(serverId, connection);
      return { connections: newConnections };
    });
    
    // 连接
    await client.connect();
    
    return connection;
  },
  
  // 关闭连接
  closeConnection: (serverId) => {
    const connection = get().connections.get(serverId);
    if (connection?.client) {
      connection.client.close();
    }
    
    set((state) => {
      const newConnections = new Map(state.connections);
      newConnections.delete(serverId);
      return { connections: newConnections };
    });
  },
  
  // 关闭所有连接
  closeAllConnections: () => {
    get().connections.forEach((connection) => {
      connection.client?.close?.();
    });
    
    set({ connections: new Map() });
  },
  
  // 更新连接状态
  updateConnectionState: (serverId, newState) => {
    set((state) => {
      const newConnections = new Map(state.connections);
      const connection = newConnections.get(serverId);
      if (connection) {
        newConnections.set(serverId, { ...connection, state: newState });
      }
      return { connections: newConnections };
    });
  },
  
  // 模态框控制
  openModal: () => set({ isModalOpen: true }),
  closeModal: () => set({ isModalOpen: false, isFullscreen: false }),
  toggleFullscreen: () => set((state) => ({ isFullscreen: !state.isFullscreen })),
}));